/* eslint-disable class-methods-use-this */
import uuid from 'uuid/v4';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import store from '@Store';
import RxSocket from '@Services/RxSocket';
import { RxSocketUrls } from '@Constants/CommonConstants';
import MessageBroker from '@Services/MessageBroker';
import RequestUrls from '@Constants/RequestUrlsConstants';
import Breadcrumb from '@Services/Breadcrumb';
import { settings as settingsSelector } from '@Pages/Heatmap/selectors';
import { HEATMAP_VIEW_PARAMS } from '@Pages/Heatmap/constants';
import HeatmapDataService from '@Pages/Heatmap/HeatmapDataService';
import SocketBroker from '@Services/SocketBroker';
import { getCombinedRouteParams } from '@AppRoutes/AppRoutesSelectors';
import history from '@AppRoutes/history';
import { generateHeatmapPath } from '@AppRoutes/utils';

class HeatmapService {
  _isMatrixCreatedFlag;

  _heatmapData;

  _pendingRequestsAmount;

  _wrapperCallback;

  _defaultMatrix;

  _dataConstructingDefer;

  _heatmapSettings;

  _updateBreadCrumbAfterSorting = () => {
    const stateParams = getCombinedRouteParams(store.getState());
    const oldBreadcrumb = Breadcrumb.getActiveBreadcrumbForDb(stateParams.databaseId);
    if (oldBreadcrumb.sortedMatrixName !== stateParams.sortedMatrixName) {
      const breadcrumb = {
        ...stateParams,
        isHeatmapShown: true,
        heatmapResultName: stateParams.resultName,
      };
      delete breadcrumb.context;
      Breadcrumb.updateBreadcrumbs(breadcrumb);
    }
  };

  _setupMatrix = (matrixParams) => {
    Logger.info('HeatmapService - setupMatrix: ', matrixParams);
    const rowsAmount = parseInt(matrixParams.rowsMax, 10);
    const colsAmount = parseInt(matrixParams.colsMax, 10);
    const colsMaxDensity = parseInt(matrixParams.colsMaxDensity, 10);
    const rowsMaxDensity = parseInt(matrixParams.rowsMaxDensity, 10);

    // create empty arrays for headers and matrix
    // for better performance and easier navigation within virtualized parts
    Object.assign(this._heatmapData, {
      headers: {
        ...this._heatmapData?.headers,
        rows: {},
        cols: {},
      },
      data: {},
      matrixName: matrixParams.matrixName,
      rowsMaxDensity,
      colsMaxDensity,
      viewOptions: {
        ...this._heatmapData?.viewOptions,
        rowsAmount,
        colsAmount,
      },
    });

    this._dataConstructingDefer.resolve(this._heatmapData);
  };

  getMatrixRowAndCol = () => ({
    rowsMax: get(this._heatmapData, 'viewOptions.rowsAmount'),
    colsMax: get(this._heatmapData, 'viewOptions.colsAmount'),
    matrixName: get(this._heatmapData, 'matrixName'),
  });

  _onDataReceived = (data) => {
    Logger.info('HeatmapService - onDataReceived: ', data, this._isMatrixCreatedFlag);

    if (this._isMatrixCreatedFlag) {
      HeatmapDataService.parseHeatmapData(data, this._heatmapData);

      if (data.payload.data) {
        this._pendingRequestsAmount -= 1;
      }

      if (this._wrapperCallback) {
        this._wrapperCallback();
      }
    } else {
      this._defaultMatrix = cloneDeep(data.payload);
      const { sortedMatrixName } = getCombinedRouteParams(store.getState());

      if (sortedMatrixName) {
        this._updateBreadCrumbAfterSorting();
        this._defaultMatrix.matrixName = sortedMatrixName;
      }

      this._setupMatrix(this._defaultMatrix);
      this._isMatrixCreatedFlag = true;
    }
  };

  _getCurrentSettings = () => {
    const settings = settingsSelector(store.getState());
    return settings && { ...settings };
  };

  _generateHeatmapDataInstance = () => ({
    data: [],
    headers: {
      rows: [],
      cols: [],
    },
    viewOptions: {
      colsHeaderHeight: HEATMAP_VIEW_PARAMS.colsHeaderHeight,
      rowsHeaderWidth: HEATMAP_VIEW_PARAMS.rowsHeaderWidth,
      cellWidth: HEATMAP_VIEW_PARAMS.cellWidth,
      cellHeight:
        this._heatmapSettings?.showSubstances.indexOf(HEATMAP_VIEW_PARAMS.substanceDrawingType) !== -1 &&
        this._heatmapSettings?.row.value === HEATMAP_VIEW_PARAMS.substanceType
          ? HEATMAP_VIEW_PARAMS.cellHeightWithStructure
          : HEATMAP_VIEW_PARAMS.cellHeight,
      colsAmount: 300,
      rowsAmount: 300,
      viewBufferSize: HEATMAP_VIEW_PARAMS.viewBufferSize,
      dataBufferSize: HEATMAP_VIEW_PARAMS.dataBufferSize,
      scrollDebounce: HEATMAP_VIEW_PARAMS.scrollDebounce,
      resizeDebounce: HEATMAP_VIEW_PARAMS.resizeDebounce,
      resizeInProgressDebounce: HEATMAP_VIEW_PARAMS.resizeInProgressDebounce,
      viewChangeDebounce: HEATMAP_VIEW_PARAMS.viewChangeDebounce,
      showStructure: this._heatmapSettings?.showSubstances.indexOf(HEATMAP_VIEW_PARAMS.substanceDrawingType) !== -1,
      substanceType: HEATMAP_VIEW_PARAMS.substanceType,
      targetType: HEATMAP_VIEW_PARAMS.targetType,
      boundaries: {
        minCellWidth:
          this._heatmapSettings.showSubstances.indexOf(HEATMAP_VIEW_PARAMS.substanceDrawingType) !== -1 &&
          this._heatmapSettings.col.value === HEATMAP_VIEW_PARAMS.substanceType
            ? HEATMAP_VIEW_PARAMS.cellWidthWithStructure
            : HEATMAP_VIEW_PARAMS.cellWidth,
        minCellHeight:
          this._heatmapSettings.showSubstances.indexOf(HEATMAP_VIEW_PARAMS.substanceDrawingType) !== -1 &&
          this._heatmapSettings.row.value === HEATMAP_VIEW_PARAMS.substanceType
            ? HEATMAP_VIEW_PARAMS.cellHeightWithStructure
            : HEATMAP_VIEW_PARAMS.cellHeight,
        maxDensityDiameter: HEATMAP_VIEW_PARAMS.maxDensityDiameter,
        minDensityDiameter: HEATMAP_VIEW_PARAMS.minDensityDiameter,
      },
    },
    matrixName: null,
    rowsMaxDensity: null,
    colsMaxDensity: null,
  });

  _cancelPreviousRequests = (matrixName) => {
    this._pendingRequestsAmount = 0;

    const payload = {
      matrixName,
    };
    return MessageBroker.send(RequestUrls.cancelHeatmapRequests, payload);
  };

  _resetData = () => {
    const previousMatrix = this._heatmapData ? this._heatmapData.matrixName : null;

    this._isMatrixCreatedFlag = false;
    this._heatmapSettings = this._getCurrentSettings();
    this._heatmapData = this._generateHeatmapDataInstance();
    HeatmapDataService.reset();

    this._dataConstructingDefer = {};
    const promise = new Promise((resolve, reject) => {
      Object.assign(this._dataConstructingDefer, { resolve, reject });
    });
    Object.assign(this._dataConstructingDefer, { promise });

    this._pendingRequestsAmount = 0;

    if (previousMatrix) {
      return this._cancelPreviousRequests(previousMatrix);
    }
    return Promise.resolve();
  };

  _sendCreateMatrixRequest = (payload) => {
    RxSocket.send(RxSocketUrls.ChannelsUrls.heatmap.create, payload);
  };

  _fetchData = (payload, isRefreshData) => {
    const sendFetchDataRequest = () => {
      this._pendingRequestsAmount += 1;
      RxSocket.send(RxSocketUrls.ChannelsUrls.heatmap.retreive, payload);
    };

    Logger.info('HeatmapService - fetchData: ', payload, isRefreshData);

    if (!payload.matrixName) {
      return;
    }

    this._heatmapData.rowsLoaded = !payload.rows.headers;
    this._heatmapData.colsLoaded = !payload.cols.headers;
    this._heatmapData.matrixLoaded = false;

    if (isRefreshData && this._pendingRequestsAmount) {
      this._cancelPreviousRequests(this._heatmapData.matrixName).then(sendFetchDataRequest).catch(Logger.error);
    } else {
      sendFetchDataRequest();
    }
  };

  /* Main matrix loading logic */
  init = () => SocketBroker.observe(RxSocketUrls.ChannelsUrls.heatmap.info, this._onDataReceived);

  createMatrix = (stateParams) =>
    this._resetData()
      .then(() => {
        const payload = {
          requestId: uuid(),
          rowsType: this._heatmapSettings.row.value,
          colsType: this._heatmapSettings.col.value,
          cellOperation: this._heatmapSettings.cellOperation,
          hitSetKey: stateParams.hitSetKey,
          resultName: stateParams.resultName,
        };

        Logger.info('HeatmapService - create matrix: ', payload);
        this._sendCreateMatrixRequest(payload);

        return this._dataConstructingDefer.promise;
      })
      .catch(Logger.reject);

  registerWrapperCallback = (callback) => {
    this._wrapperCallback = callback;

    return () => {
      this._wrapperCallback = null;
    };
  };

  onInitData = (rowsToShow, colsToShow) => {
    HeatmapDataService.onInitData(this._heatmapData, rowsToShow, colsToShow, this._fetchData);
  };

  getHeatmapData = () => this._heatmapData;

  onRequestData = (newRowStart, newCellStart, rowsToShow, colsToShow) => {
    HeatmapDataService.onRequestData(this._heatmapData, newRowStart, newCellStart, rowsToShow, colsToShow, this._fetchData);
  };

  /* Sort by activity */
  getSortParams = () => {
    const stateParams = getCombinedRouteParams(store.getState());

    return stateParams.index
      ? {
          index: parseInt(stateParams.index, 10),
          ascending: stateParams.ascending,
          isRow: stateParams.isRow,
        }
      : null;
  };

  sortMatrixByActivity = (index, isColumn, ascending) => {
    const payload = {
      matrixName: this._heatmapData.matrixName,
      index,
      column: isColumn,
      ascending,
    };

    return this._resetData()
      .then(() =>
        MessageBroker.send(RequestUrls.heatmapSort, payload)
          .then((data) => {
            const params = getCombinedRouteParams(store.getState());
            params.sortedMatrixName = data.sortedMatrixName;
            params.index = index;
            params.ascending = ascending;
            params.isHeatmap = true;
            params.isRow = !isColumn;
            params.origSortedMatrixName = data.origSortedMatrixName;
            const pathName = generateHeatmapPath(params);
            history.push({
              pathname: pathName,
              state: params,
            });
          })
          .catch(Logger.reject)
      )
      .catch(Logger.reject);
  };

  resetSortByActivity = () =>
    this._resetData()
      .then(() => {
        const params = getCombinedRouteParams(store.getState());
        params.sortedMatrixName = null;
        params.index = null;
        params.ascending = null;
        params.isRow = null;
        params.isHeatmap = true;
        const pathName = generateHeatmapPath(params);
        history.push({
          pathname: pathName,
          state: params,
        });
      })
      .catch(Logger.error);

  /* Limit / Exclude */
  sendExcludeRequest = (payload) =>
    this._resetData()
      .then(() => MessageBroker.send(RequestUrls.filterHeatmap, payload))
      .catch(Logger.reject);
}

export default new HeatmapService();
